import React, {
  memo,
  RefObject,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import {
  filterCustomCodes,
  normalizeCustomServiceCodeOption,
  visitNoteFieldDirtyClasses,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  customServiceLookupClasses,
  visitNoteCustomServiceActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';
import type {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import type {
  QueryObserverResult,
} from '@tanstack/react-query';
import type {
  FilterOptionsState,
} from '@mui/material/useAutocomplete';

type Props = {
  patientId: number;
  visitNoteId: number;
  sectionRef?: RefObject<HTMLDivElement>;
  favorites?: VisitNoteServiceCode[];
  refetchFavoriteCode?: () => Promise<QueryObserverResult>;
  saveVisitNote?: () => Promise<void>;
};

type SectionProps = Props & {
  field: FieldValues & { onChange: (value?: VisitNoteServiceCode[]) => void };
  isDirty: boolean;
};

function CustomServiceCodesSectionContent({
  field,
  isDirty,
  sectionRef,
  ...props
}: SectionProps) {
  const classes = useMemo(() => ({
    ...customServiceLookupClasses,
    container: clsx({ [visitNoteFieldDirtyClasses]: isDirty }),
  }), [isDirty]);

  return (
    <ServicesLookup
      {...field}
      type="CustomCode"
      label="Custom Services"
      dropdownId="custom-code-selection"
      id="customServiceCodes"
      containerRef={sectionRef}
      placeholder="Search Custom Services"
      visitNoteId={props.visitNoteId}
      patientId={props.patientId}
      externalFilter={filterCustomCodes as (
        options: VisitNoteServiceCode[],
        state: FilterOptionsState<VisitNoteServiceCode>,
      ) => VisitNoteServiceCode[]}
      favorites={props.favorites ?? []}
      refetchFavoriteCode={props.refetchFavoriteCode}
      classes={classes}
      className="pb-6"
      actions={visitNoteCustomServiceActions}
      normalizeOption={normalizeCustomServiceCodeOption}
    />
  );
}

const CustomServiceCodesSectionContentMemoized = memo(
  CustomServiceCodesSectionContent,
);

function CustomServiceCodesSection(props: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name="bill.customServiceCodes"
      control={control}
      render={({ field, fieldState: { isDirty } }) => (
        <CustomServiceCodesSectionContentMemoized
          {...props}
          field={field}
          isDirty={isDirty}
        />
      )}
    />
  );
}

export default memo(CustomServiceCodesSection);
